<template>
  <div class="alice-rec">
    <!-- ============== Sub-page header ============== -->
    <header class="alice-rec__head">
      <div class="alice-rec__head-main">
        <div class="alice-rec__crumb">
          <span>Project</span>
          <Icon name="chevron-right" :size="12" />
          <span>Recordings</span>
        </div>
        <h1 class="alice-rec__title">
          Recordings
          <span class="alice-rec__count">{{ recordings.length }}</span>
        </h1>
        <p class="alice-rec__subtitle">
          Sessie-opnames van de interviews. Drop nieuwe bestanden in de zone
          hieronder of klik om te uploaden.
        </p>
      </div>

      <div class="alice-rec__head-actions">
        <button
          v-if="!isGuest"
          class="alice-rec__export-all"
          :disabled="exportingAll || !recordings.length"
          @click="exportAllMoments"
        >
          <Icon name="arrow-right" :size="16" />
          <span>{{ exportingAll ? "Exporting…" : "Export all moments" }}</span>
        </button>
        <button
          v-if="!isGuest"
          class="alice-rec__upload"
          :disabled="anyUploading"
          @click="triggerUpload"
        >
          <Icon name="plus" :size="16" />
          <span>Upload file</span>
        </button>
      </div>
      <input
        ref="fileInput"
        type="file"
        :accept="recordingAccept"
        multiple
        style="display: none"
        @change="onFileChange"
      />
    </header>

    <!-- ============== Stats trio ============== -->
    <div class="alice-rec__stats">
      <div class="alice-rec__stat-tile">
        <div class="alice-rec__stat-row">
          <span
            class="alice-rec__stat-mark"
            style="background: var(--vp-black)"
          />
          <span class="alice-rec__stat-value">{{ recordings.length }}</span>
        </div>
        <div class="alice-rec__stat-label">Total recordings</div>
        <div class="alice-rec__stat-sub">{{ totalSubLabel }}</div>
      </div>
      <div class="alice-rec__stat-tile">
        <div class="alice-rec__stat-row">
          <span
            class="alice-rec__stat-mark"
            style="background: var(--vp-geel-100)"
          />
          <span class="alice-rec__stat-value alice-rec__stat-value--mono">{{
            totalDurationLabel
          }}</span>
        </div>
        <div class="alice-rec__stat-label">Total duration</div>
        <div class="alice-rec__stat-sub">across sessions</div>
      </div>
      <div class="alice-rec__stat-tile">
        <div class="alice-rec__stat-row">
          <span
            class="alice-rec__stat-mark"
            style="background: var(--vp-grijs-60)"
          />
          <span class="alice-rec__stat-value">{{ storageLabel }}</span>
        </div>
        <div class="alice-rec__stat-label">Storage used</div>
        <div class="alice-rec__stat-sub">of 50 GB workspace</div>
      </div>
    </div>

    <!-- ============== Toolbar ============== -->
    <div class="alice-rec__toolbar">
      <div class="alice-rec__search">
        <Icon name="search" :size="18" />
        <input
          v-model="search"
          class="alice-rec__search-input"
          type="text"
          placeholder="Search recordings, participants…"
        />
        <span class="alice-rec__search-count"
          >{{ filtered.length }} of {{ recordings.length }}</span
        >
      </div>
      <button class="alice-rec__sort" @click="cycleSort">
        <Icon name="sort" :size="18" />
        <span>{{ sortLabel }}</span>
        <Icon name="chevron-down" :size="14" />
      </button>
    </div>

    <!-- ============== Upload progress (in-flight) ============== -->
    <div v-if="anyUploading" class="alice-rec__uploads">
      <div
        v-for="(item, idx) in uploading.filter((u) => u.uploading)"
        :key="`upload-${idx}-${item.name}`"
        class="alice-rec__upload-row"
      >
        <span class="alice-rec__upload-name">{{ item.name }}</span>
        <div class="alice-rec__upload-track">
          <div
            class="alice-rec__upload-fill"
            :style="{ width: `${item.progress}%` }"
          />
        </div>
        <span class="alice-rec__upload-pct"
          >{{ Math.round(item.progress) }}%</span
        >
      </div>
    </div>

    <!-- ============== Table ============== -->
    <div class="alice-rec__table">
      <div class="alice-rec__table-head">
        <div>#</div>
        <div>Name</div>
        <div>Audio</div>
        <div>Duration</div>
        <div>Created</div>
        <div>Status</div>
        <div></div>
      </div>

      <div v-if="!filtered.length" class="alice-rec__empty">
        <p v-if="recordings.length">
          No recordings match
          <span class="alice-rec__empty-q">"{{ search }}"</span>.
        </p>
        <p v-else>
          No recordings yet. Drop session files in the zone below or click
          “Upload file”.
        </p>
      </div>

      <div
        v-for="(rec, idx) in filtered"
        :key="rec.uid || rec.id || idx"
        class="alice-rec__row"
        :class="{
          'alice-rec__row--selected':
            selectedRecording && recId(selectedRecording) === recId(rec),
          'is-stagger-in': staggerIn,
        }"
        :style="
          staggerIn ? { animationDelay: Math.min(idx, 7) * 40 + 'ms' } : null
        "
        role="button"
        tabindex="0"
        @click="selectRecording(rec)"
        @keydown.enter="selectRecording(rec)"
      >
        <div class="alice-rec__cell-num">
          {{ String(idx + 1).padStart(2, "0") }}
        </div>

        <div class="alice-rec__cell-name">
          <div class="alice-rec__name">{{ rec.name }}</div>
          <div v-if="participantLine(rec)" class="alice-rec__name-meta">
            {{ participantLine(rec) }}
          </div>
          <div class="alice-rec__tags">
            <span
              v-for="(t, ti) in rec.tags || []"
              :key="ti"
              class="alice-rec__tag"
            >
              <span class="alice-rec__tag-key">{{ t.key }}</span>
              {{ t.value }}
            </span>
            <button
              class="alice-rec__tag-edit"
              :title="rec.tags && rec.tags.length ? 'Edit tags' : 'Add tags'"
              @click.stop="openTagDialog(rec)"
            >
              <Icon
                :name="rec.tags && rec.tags.length ? 'pencil' : 'plus'"
                :size="11"
              />
              <span>{{
                rec.tags && rec.tags.length ? "Edit tags" : "Add tags"
              }}</span>
            </button>
          </div>
        </div>

        <div class="alice-rec__cell-audio">
          <Waveform :seed="rec.uid || rec.name || ''" />
          <span class="alice-rec__audio-meta">
            <span>{{ formatLabel(rec.contentType) }}</span>
            <span class="alice-rec__audio-sep">·</span>
            <span v-if="rec.videoDeleted" class="alice-rec__audio-gone"
              >{{ mediaWord(rec) }} removed after 90 days</span
            >
            <span v-else>{{ formatBytes(rec.size) }}</span>
          </span>
        </div>

        <div class="alice-rec__cell-duration">{{ durationLabel(rec) }}</div>

        <div class="alice-rec__cell-created">
          <div>{{ createdDate(rec.createdAt) }}</div>
          <div v-if="createdTime(rec.createdAt)" class="alice-rec__created-sub">
            {{ createdTime(rec.createdAt) }}
            <template v-if="rec.uploadedBy"> · {{ rec.uploadedBy }}</template>
          </div>
        </div>

        <div class="alice-rec__cell-status">
          <span
            class="alice-rec__status"
            :class="`alice-rec__status--${recordingStatus(rec).key}`"
            :title="recordingStatus(rec).hint || null"
          >
            <span class="alice-rec__status-dot" />
            {{ recordingStatus(rec).label }}
          </span>
          <button
            class="alice-rec__open"
            :title="isProcessed(rec) ? 'Open transcript' : 'View progress'"
            @click.stop="selectRecording(rec)"
          >
            <Icon name="arrow-right" :size="14" />
          </button>
        </div>

        <div class="alice-rec__cell-actions">
          <button
            v-if="rec.moments && !isGuest"
            class="alice-rec__action"
            title="Download moments"
            @click.stop="downloadMoments(rec)"
          >
            <Icon name="arrow-right" :size="14" />
          </button>
          <button
            v-if="!isGuest"
            class="alice-rec__action alice-rec__action--danger"
            title="Delete"
            @click.stop="deleteFile(rec)"
          >
            <Icon name="trash" :size="14" />
          </button>
          <div class="alice-rec__menu-wrap">
            <button
              class="alice-rec__action"
              :class="{ 'is-active': openMenuFor === rec.uid }"
              title="More actions"
              aria-haspopup="menu"
              v-if="!isGuest"
              :aria-expanded="openMenuFor === rec.uid ? 'true' : 'false'"
              @click.stop="toggleRowMenu(rec)"
            >
              <Icon name="more" :size="14" />
            </button>
            <div
              v-if="openMenuFor === rec.uid"
              class="alice-rec__menu"
              role="menu"
              @click.stop
            >
              <button
                class="alice-rec__menu-item"
                role="menuitem"
                :disabled="rerunBusy === rec.uid"
                @click.stop="rerunMissingChunks(rec)"
              >
                Rerun missing chunks
              </button>
              <button
                v-if="!rec.videoDeleted"
                class="alice-rec__menu-item alice-rec__menu-item--danger"
                role="menuitem"
                :disabled="rerunBusy === rec.uid"
                @click.stop="rerunFullRecording(rec)"
              >
                Rerun full recording
              </button>
              <!-- No source file left, so a full rerun cannot work. -->
              <div v-else class="alice-rec__menu-note">
                {{ mediaWord(rec, true) }} removed after 90 days
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Click-away catcher for the row "⋯" menu -->
    <div
      v-if="openMenuFor"
      class="alice-rec__menu-backdrop"
      @click="closeRowMenu"
    />

    <!-- ============== Drop zone ============== -->
    <div
      class="alice-rec__dropzone"
      :class="{ 'is-dragover': dragOver }"
      @click="triggerUpload"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <Icon name="plus" :size="20" />
      <div class="alice-rec__dropzone-text">
        Drop video or audio files here, or
        <span class="alice-rec__dropzone-link">click to browse</span>
      </div>
      <div class="alice-rec__dropzone-sub">
        Video (MP4, M4V) or audio (MP3, M4A, WAV) · multiple at once · deleted
        after 90 days
      </div>
    </div>

    <!-- ============== Moments side drawer ============== -->
    <transition name="alice-drawer">
      <aside
        v-if="selectedRecording"
        class="alice-rec__drawer"
        aria-label="Moments"
      >
        <header class="alice-rec__drawer-head">
          <div>
            <div class="alice-rec__drawer-eyebrow">Moments</div>
            <h2 class="alice-rec__drawer-title">
              {{ selectedRecording.name }}
            </h2>
            <div class="alice-rec__drawer-meta">
              {{ sortedMoments.length }} moment{{
                sortedMoments.length === 1 ? "" : "s"
              }}
            </div>
          </div>
          <div class="alice-rec__drawer-actions">
            <button
              v-if="!isGuest"
              class="alice-rec__drawer-export"
              title="Export moments as JSON"
              :disabled="!sortedMoments.length || momentsLoading"
              @click="exportSelectedMoments"
            >
              Export JSON
            </button>
            <button
              class="alice-rec__drawer-close"
              title="Close"
              @click="closeMomentsPanel"
            >
              ×
            </button>
          </div>
        </header>

        <div class="alice-rec__drawer-body">
          <div v-if="momentsLoading" class="alice-rec__drawer-status">
            <span class="alice-rec__drawer-status-dot" />
            <span>Loading moments…</span>
          </div>
          <div
            v-else-if="!sortedMoments.length"
            class="alice-rec__drawer-status"
          >
            {{
              recordingStatus(selectedRecording).key === "failed"
                ? recordingStatus(selectedRecording).hint
                : "No moments found for this recording."
            }}
          </div>
          <article
            v-for="(m, i) in sortedMoments"
            :key="i"
            class="alice-rec__moment"
          >
            <div class="alice-rec__moment-time">
              {{ m.time_start }} — {{ m.time_end }}
            </div>
            <div v-if="m.lead" class="alice-rec__moment-lead">{{ m.lead }}</div>
            <dl class="alice-rec__moment-fields">
              <template v-if="m.behavior_result">
                <dt>Behavior</dt>
                <dd>{{ m.behavior_result }}</dd>
              </template>
              <template v-if="m.feedback_result">
                <dt>Feedback</dt>
                <dd>{{ m.feedback_result }}</dd>
              </template>
              <template v-if="m.reasoning">
                <dt>Reasoning</dt>
                <dd>{{ m.reasoning }}</dd>
              </template>
            </dl>
            <div
              v-if="m.quotes && m.quotes.length"
              class="alice-rec__moment-quotes"
            >
              <p v-for="(q, qi) in m.quotes" :key="qi">"{{ q }}"</p>
            </div>
            <div
              v-if="m.importance || m.location"
              class="alice-rec__moment-tags"
            >
              <span v-if="m.importance" class="alice-rec__moment-tag">{{
                m.importance
              }}</span>
              <span v-if="m.location" class="alice-rec__moment-tag">{{
                m.location
              }}</span>
            </div>
          </article>
        </div>
      </aside>
    </transition>

    <!-- ============== Tags editor modal ============== -->
    <RecordingTagsDialog
      :dialog="tagDialog"
      :recording="tagEditingRec"
      :existing-keys="existingTagKeys"
      :saving="tagSaving"
      @close="closeTagDialog"
      @save="saveTags"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { storage, db, functions } from "@/firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  onSnapshot,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { httpsCallable } from "firebase/functions";

import Icon from "@/components/alice/Icon.vue";
import Waveform from "@/components/alice/Waveform.vue";
import RecordingTagsDialog from "@/components/RecordingTagsDialog.vue";
import { recordingStatus, recordingsSummary } from "@/utils/recordingStatus.js";
import {
  RECORDING_ACCEPT,
  recordingContentType,
  isAudioRecording,
  mediaLabel,
} from "@/utils/recordingMedia.js";

// Uploads outlive this page: moving elsewhere in Alice keeps them running, but
// closing or reloading the tab kills them, and the recording never arrives.
// Count them at module level so the leave-site warning stays armed until the
// last one finishes, even after this page has unmounted.
let uploadsInFlight = 0;
function warnBeforeUnload(e) {
  e.preventDefault();
  e.returnValue = "";
}
function trackUpload(delta) {
  uploadsInFlight += delta;
  if (delta > 0 && uploadsInFlight === 1) {
    window.addEventListener("beforeunload", warnBeforeUnload);
  } else if (uploadsInFlight === 0) {
    window.removeEventListener("beforeunload", warnBeforeUnload);
  }
}

const SORTS = [
  { key: "recent", label: "Most recent" },
  { key: "oldest", label: "Oldest first" },
  { key: "alpha", label: "A → Z" },
];

function formatHMS(totalSeconds) {
  if (!totalSeconds || !Number.isFinite(totalSeconds)) return "—";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export default {
  name: "ProjectRecordings",
  components: { Icon, Waveform, RecordingTagsDialog },
  data() {
    return {
      recordings: [],
      unsubscribe: null,
      uploading: [],
      search: "",
      sortIndex: 0,
      dragOver: false,
      selectedRecording: null,
      moments: [],
      momentsUnsubscribe: null,
      momentsLoading: false,
      exportingAll: false,
      staggerIn: true,
      openMenuFor: null,
      rerunBusy: null,
      recordingAccept: RECORDING_ACCEPT,
      // Tags editor modal
      tagDialog: false,
      tagEditingRec: null,
      tagSaving: false,
    };
  },
  computed: {
    ...mapState({
      project: (state) => state.project,
    }),
    isGuest() {
      return this.$store.getters.isGuest;
    },
    sortLabel() {
      return SORTS[this.sortIndex].label;
    },
    sortKey() {
      return SORTS[this.sortIndex].key;
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      let list = this.recordings;
      if (q) {
        list = list.filter((r) => {
          const name = (r.name || "").toLowerCase();
          const participant = (r.participant_name || "").toLowerCase();
          return name.includes(q) || participant.includes(q);
        });
      }
      const copy = [...list];
      if (this.sortKey === "alpha") {
        copy.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      } else if (this.sortKey === "oldest") {
        copy.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      } else {
        copy.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      }
      return copy;
    },
    anyUploading() {
      return this.uploading.some((u) => u.uploading);
    },
    totalSubLabel() {
      return recordingsSummary(this.recordings);
    },
    totalDurationLabel() {
      const sum = this.recordings.reduce(
        (s, r) => s + (Number(r.duration_seconds) || 0),
        0
      );
      return formatHMS(sum);
    },
    // Only recordings still in Storage. A purged recording keeps `size` as
    // a record of what was there, but counting it would report space we no
    // longer pay for.
    storageLabel() {
      const bytes = this.recordings
        .filter((r) => !r.videoDeleted)
        .reduce((s, r) => s + (Number(r.size) || 0), 0);
      return this.formatBytes(bytes, 1) || "0 B";
    },
    sortedMoments() {
      return [...this.moments].sort(
        (a, b) => this.parseMmSs(a.time_start) - this.parseMmSs(b.time_start)
      );
    },
    // Distinct tag keys already in use, offered as suggestions in the editor so
    // subgroup keys stay consistent across recordings.
    existingTagKeys() {
      const set = new Set();
      this.recordings.forEach((r) => {
        (r.tags || []).forEach((t) => {
          if (t && t.key) set.add(t.key);
        });
      });
      return [...set].sort((a, b) => a.localeCompare(b));
    },
  },
  mounted() {
    const projectId = this.$route.params.id;
    if (projectId) {
      this.unsubscribe = onSnapshot(
        collection(db, "projects", projectId, "recordings"),
        (snapshot) => {
          this.recordings = snapshot.docs.map((d) => ({
            ...d.data(),
            id: d.id,
          }));
        }
      );
    }
    // Let the list cascade in once, then stop gating future re-renders.
    this._staggerTimer = setTimeout(() => {
      this.staggerIn = false;
    }, 700);
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.momentsUnsubscribe) this.momentsUnsubscribe();
    clearTimeout(this._staggerTimer);
  },
  methods: {
    cycleSort() {
      this.sortIndex = (this.sortIndex + 1) % SORTS.length;
    },
    triggerUpload() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    formatBytes(bytes, digits = 0) {
      if (!bytes || bytes < 0) return "—";
      const units = ["B", "KB", "MB", "GB", "TB"];
      let i = 0;
      let n = bytes;
      while (n >= 1024 && i < units.length - 1) {
        n /= 1024;
        i++;
      }
      const out = i >= 3 ? n.toFixed(digits || 1) : Math.round(n);
      return `${out} ${units[i]}`;
    },
    formatLabel: mediaLabel,
    mediaWord(rec, capitalized = false) {
      const word = isAudioRecording(rec) ? "audio" : "video";
      return capitalized ? word[0].toUpperCase() + word.slice(1) : word;
    },
    durationLabel(rec) {
      if (Number.isFinite(rec.duration_seconds) && rec.duration_seconds > 0) {
        return formatHMS(rec.duration_seconds);
      }
      return "—";
    },
    isProcessed(rec) {
      return rec.status === "processed" || rec.status === "transcribed";
    },
    recordingStatus,
    recId(rec) {
      return rec.uid || rec.id;
    },
    participantLine(rec) {
      const code = rec.participant_code || rec.participantId;
      const name = rec.participant_name;
      const role = rec.participant_role;
      const parts = [];
      if (code) parts.push(code);
      if (name) parts.push(role ? `${name} (${role})` : name);
      return parts.join(" · ");
    },
    createdDate(unix) {
      if (!unix) return "—";
      const d = new Date(unix * 1000);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    createdTime(unix) {
      if (!unix) return "";
      const d = new Date(unix * 1000);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    parseMmSs(s) {
      if (!s) return 0;
      const parts = String(s).split(":");
      if (parts.length === 2) {
        return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
      }
      return 0;
    },

    selectRecording(rec) {
      if (
        this.selectedRecording &&
        this.recId(this.selectedRecording) === this.recId(rec)
      ) {
        this.closeMomentsPanel();
        return;
      }
      if (this.momentsUnsubscribe) this.momentsUnsubscribe();
      this.selectedRecording = rec;
      this.moments = [];
      this.momentsLoading = true;

      const projectId = this.$route.params.id;
      if (!rec.uid) {
        this.momentsLoading = false;
        return;
      }
      this.momentsUnsubscribe = onSnapshot(
        collection(db, "projects", projectId, "recordings", rec.uid, "moments"),
        (snapshot) => {
          this.moments = snapshot.docs.map((d) => ({
            ...d.data(),
            id: d.id,
          }));
          this.momentsLoading = false;
        }
      );
    },
    closeMomentsPanel() {
      if (this.momentsUnsubscribe) {
        this.momentsUnsubscribe();
        this.momentsUnsubscribe = null;
      }
      this.selectedRecording = null;
      this.moments = [];
    },
    exportSelectedMoments() {
      if (!this.selectedRecording) return;
      const blob = new Blob([JSON.stringify(this.sortedMoments, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${this.selectedRecording.name}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    async exportAllMoments() {
      if (this.exportingAll || !this.recordings.length) return;
      this.exportingAll = true;
      try {
        const projectId = this.$route.params.id;
        const results = await Promise.all(
          this.recordings
            .filter((rec) => rec.uid)
            .map(async (rec) => {
              const snap = await getDocs(
                collection(
                  db,
                  "projects",
                  projectId,
                  "recordings",
                  rec.uid,
                  "moments"
                )
              );
              return {
                recording: {
                  id: rec.uid,
                  name: rec.name,
                  createdAt: rec.createdAt || null,
                  participant_code: rec.participant_code || null,
                  participant_name: rec.participant_name || null,
                },
                moments: snap.docs.map((d) => d.data()),
              };
            })
        );
        const projectName = this.project?.name || "project";
        const blob = new Blob([JSON.stringify(results, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${projectName}-all-moments.json`;
        a.click();
        URL.revokeObjectURL(url);
      } finally {
        this.exportingAll = false;
      }
    },
    async downloadMoments(rec) {
      const momentsQuery = query(
        collection(
          db,
          "projects",
          this.$route.params.id,
          "recordings",
          rec.uid,
          "moments"
        )
      );
      const momentsSnap = await getDocs(momentsQuery);
      const data = momentsSnap.docs.map((d) => d.data());
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rec.name}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },

    async uploadFile(file, contentType) {
      // Take the slot from push() itself: callers start several uploads in one
      // loop, so any index computed beforehand drifts as this array grows.
      const index =
        this.uploading.push({
          uploading: true,
          progress: 0,
          name: file.name || `Upload ${this.uploading.length + 1}`,
        }) - 1;
      const projectId = this.$route.params.id || "unknown";
      let docRef = null;
      let uploaded = false;
      trackUpload(1);
      try {
        docRef = await addDoc(
          collection(db, "projects", projectId, "recordings"),
          {
            size: file.size,
            contentType,
            status: "pending",
            createdAt: Math.round(Date.now() / 1000),
            name: file.name.split(".")[0],
          }
        );

        const path = `projects/${projectId}/recordings/${docRef.id}.${file.name
          .split(".")
          .pop()}`;
        const refObj = storageRef(storage, path);
        const uploadTask = uploadBytesResumable(refObj, file, { contentType });

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            this.uploading[index].progress = progress;
            if (progress === 100) {
              this.uploading[index].progress = 0;
              this.uploading[index].uploading = false;
            }
          },
          (error) => {
            console.error("Upload error", error);
          }
        );

        await uploadTask;
        uploaded = true;
        const downloadURL = await getDownloadURL(refObj);
        await updateDoc(
          doc(db, "projects", projectId, "recordings", docRef.id),
          {
            uid: docRef.id,
            path,
            url: downloadURL,
          }
        );
        await this.bumpStatusToInProgress(projectId);
        this.$store.dispatch("fetchProject", projectId);
      } catch (err) {
        console.error("Upload failed", err);
        // Only when the file itself never arrived: once it's in Storage,
        // onRecordingUploaded owns the status, even if a later step here failed.
        if (docRef && !uploaded) {
          await updateDoc(docRef, {
            status: "upload_failed",
            error: `The upload stopped (${
              err.code || err.message || err
            }). Delete this recording and upload it again.`,
          }).catch((e) => console.error("Couldn't mark upload failed", e));
        }
      } finally {
        trackUpload(-1);
        if (this.uploading[index]) this.uploading[index].uploading = false;
      }
    },

    async bumpStatusToInProgress(projectId) {
      const current = this.$store.state.project?.status;
      if (current && current !== "draft") return;
      try {
        await updateDoc(doc(db, "projects", projectId), {
          status: "in_progress",
        });
      } catch (err) {
        console.error("Failed to bump project status:", err);
      }
    },
    onFileChange(e) {
      const files = Array.from(e.target.files || []);
      files.forEach((file) => {
        if (!file) return;
        const contentType = recordingContentType(file);
        if (!contentType) {
          alert(`${file.name} is not a video or audio file`);
          return;
        }
        this.uploadFile(file, contentType);
      });
      e.target.value = "";
    },
    onDrop(event) {
      this.dragOver = false;
      const files = Array.from(event.dataTransfer?.files || []);
      files.forEach((file) => {
        const contentType = recordingContentType(file);
        if (!contentType) return;
        this.uploadFile(file, contentType);
      });
    },

    async deleteFile(rec) {
      if (!confirm(`Delete ${rec.name}?`)) return;
      // Failed uploads have no `uid` (the page writes it only once the upload
      // completes), so fall back to the doc id — otherwise delete threw on an
      // undefined path and those rows could never be removed.
      const id = this.recId(rec);
      try {
        const projectId = this.$route.params.id;
        const momentsQuery = query(
          collection(db, "projects", projectId, "recordings", id, "moments")
        );
        const momentsSnap = await getDocs(momentsQuery);
        await Promise.all(
          momentsSnap.docs.map(async (d) => {
            const transcriptQuery = query(
              collection(
                db,
                "projects",
                projectId,
                "recordings",
                id,
                "moments",
                d.id,
                "transcript"
              )
            );
            const transcriptSnap = await getDocs(transcriptQuery);
            await Promise.all(
              transcriptSnap.docs.map((t) =>
                deleteDoc(
                  doc(
                    db,
                    "projects",
                    projectId,
                    "recordings",
                    id,
                    "moments",
                    d.id,
                    "transcript",
                    t.id
                  )
                )
              )
            );
            await deleteDoc(
              doc(db, "projects", projectId, "recordings", id, "moments", d.id)
            );
          })
        );
        await deleteDoc(doc(db, "projects", projectId, "recordings", id));

        if (
          this.selectedRecording &&
          this.recId(this.selectedRecording) === id
        ) {
          this.closeMomentsPanel();
        }
        this.$store.dispatch("fetchProject", projectId);
      } catch (err) {
        console.error("Delete failed", err);
        alert(`Couldn't delete ${rec.name}: ${err.message || err}`);
      }
    },

    openTagDialog(rec) {
      this.tagEditingRec = rec;
      this.tagDialog = true;
    },
    closeTagDialog() {
      this.tagDialog = false;
      this.tagEditingRec = null;
    },
    async saveTags(tags) {
      const rec = this.tagEditingRec;
      const recId = rec?.uid || rec?.id;
      if (!recId) {
        this.closeTagDialog();
        return;
      }
      this.tagSaving = true;
      try {
        const projectId = this.$route.params.id;
        await updateDoc(doc(db, "projects", projectId, "recordings", recId), {
          tags,
        });
        // The table re-renders via onSnapshot; refresh the store so the chat
        // context picker reflects the new tags too.
        this.$store.dispatch("fetchProject", projectId);
        this.closeTagDialog();
      } catch (err) {
        console.error("Saving tags failed", err);
        alert(`Couldn't save tags: ${err.message || err}`);
      } finally {
        this.tagSaving = false;
      }
    },
    toggleRowMenu(rec) {
      this.openMenuFor = this.openMenuFor === rec.uid ? null : rec.uid;
    },
    closeRowMenu() {
      this.openMenuFor = null;
    },
    async rerunMissingChunks(rec) {
      this.closeRowMenu();
      if (!rec.uid || this.rerunBusy) return;
      if (
        !confirm(
          `Rerun the missing chunks for "${rec.name}"?\n\nOnly chunks that never finished are reprocessed; already-transcribed chunks are left untouched.`
        )
      )
        return;
      this.rerunBusy = rec.uid;
      try {
        const fn = httpsCallable(functions, "rerunMissingChunks");
        const res = await fn({
          projectId: this.$route.params.id,
          recordingId: rec.uid,
        });
        const n = res?.data?.requeued ?? 0;
        alert(
          n
            ? `Re-queued ${n} missing chunk${
                n === 1 ? "" : "s"
              }. Status is back to “processing”.`
            : "No missing chunks were found — nothing to rerun."
        );
      } catch (err) {
        console.error("Rerun missing chunks failed", err);
        alert(`Couldn't rerun missing chunks: ${err.message || err}`);
      } finally {
        this.rerunBusy = null;
      }
    },
    async rerunFullRecording(rec) {
      this.closeRowMenu();
      if (!rec.uid || this.rerunBusy) return;
      if (
        !confirm(
          `Rerun the FULL recording for "${rec.name}"?\n\nThis deletes all existing moments for this recording and reprocesses the original file from scratch.`
        )
      )
        return;
      this.rerunBusy = rec.uid;
      try {
        const fn = httpsCallable(functions, "rerunFullRecording");
        await fn({
          projectId: this.$route.params.id,
          recordingId: rec.uid,
        });
        alert(
          "Full rerun started. The recording will reprocess from the original file."
        );
      } catch (err) {
        console.error("Rerun full recording failed", err);
        alert(`Couldn't rerun the recording: ${err.message || err}`);
      } finally {
        this.rerunBusy = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.alice-rec {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-rec__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-rec__head-main {
  flex: 1;
  min-width: 0;
}
.alice-rec__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-rec__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-rec__count {
  display: inline-block;
  margin-left: 12px;
  font-family: var(--font-ui);
  font-size: 16px;
  font-weight: 700;
  color: var(--fg-3);
  vertical-align: middle;
}
.alice-rec__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 560px;
  line-height: 1.5;
}
.alice-rec__head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.alice-rec__upload {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 20px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-rec__upload:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-rec__upload:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-rec__export-all {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 18px;
  background: transparent;
  color: var(--vp-black);
  border: 1px solid var(--vp-black);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-rec__export-all:hover:not(:disabled) {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-rec__export-all:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ============== Stats ============== */
.alice-rec__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid var(--border-hairline);
}
.alice-rec__stat-tile {
  position: relative;
  padding: 20px 32px;
}
.alice-rec__stat-tile + .alice-rec__stat-tile {
  border-left: 1px solid var(--border-hairline);
}
.alice-rec__stat-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.alice-rec__stat-mark {
  width: 10px;
  height: 10px;
  align-self: center;
  flex-shrink: 0;
}
.alice-rec__stat-value {
  font-family: var(--font-display);
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--vp-black);
}
.alice-rec__stat-value--mono {
  font-family: var(--font-mono);
  font-size: 32px;
}
.alice-rec__stat-label {
  margin-top: 8px;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-rec__stat-sub {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}

/* ============== Toolbar ============== */
.alice-rec__toolbar {
  display: flex;
  align-items: stretch;
  height: 56px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-rec__search {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 24px;
  border-right: 1px solid var(--border-hairline);
  color: var(--vp-black);
}
.alice-rec__search-input {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--vp-black);
}
.alice-rec__search-input::placeholder {
  color: var(--fg-3);
}
.alice-rec__search-count {
  flex-shrink: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  white-space: nowrap;
}
.alice-rec__sort {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--vp-black);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
}
.alice-rec__sort:hover {
  background: var(--bg-muted);
}

/* ============== Uploads in flight ============== */
.alice-rec__uploads {
  display: flex;
  flex-direction: column;
  gap: 0;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--accent-20);
}
.alice-rec__upload-row {
  display: grid;
  grid-template-columns: 1fr minmax(160px, 320px) 56px;
  gap: 16px;
  align-items: center;
  padding: 12px 32px;
  font-family: var(--font-ui);
  font-size: 13px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-rec__upload-row:last-child {
  border-bottom: none;
}
.alice-rec__upload-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--vp-black);
  font-weight: 700;
}
.alice-rec__upload-track {
  height: 6px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
}
.alice-rec__upload-fill {
  height: 100%;
  background: var(--vp-black);
  transition: width 0.2s ease;
}
.alice-rec__upload-pct {
  font-family: var(--font-mono);
  font-size: 13px;
  text-align: right;
  color: var(--vp-black);
}

/* ============== Table ============== */
.alice-rec__table {
  display: flex;
  flex-direction: column;
}
.alice-rec__table-head,
.alice-rec__row {
  display: grid;
  grid-template-columns: 56px minmax(0, 2fr) minmax(0, 1.6fr) 110px 160px 200px 104px;
  gap: 16px;
  align-items: center;
  padding: 0 32px;
}
.alice-rec__table-head {
  height: 44px;
  background: var(--bg-muted);
  border-bottom: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  position: sticky;
  top: 0;
  z-index: 1;
}
.alice-rec__row {
  position: relative;
  padding-top: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border-hairline);
  cursor: pointer;
  transition: background 0.12s ease;
}
.alice-rec__row:hover,
.alice-rec__row:focus-visible {
  background: var(--bg-muted);
  outline: none;
}
.alice-rec__row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
}
.alice-rec__row:hover::before,
.alice-rec__row:focus-visible::before,
.alice-rec__row--selected::before {
  background: var(--accent-100);
}
.alice-rec__row--selected {
  background: var(--accent-20);
}

.alice-rec__cell-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 28px;
  background: var(--vp-grijs-20);
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--fg-3);
}

.alice-rec__cell-name {
  min-width: 0;
}
.alice-rec__name {
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 700;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-rec__name-meta {
  margin-top: 2px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-rec__tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.alice-rec__tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  background: var(--vp-grijs-20);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 600;
  color: var(--fg-1);
  white-space: nowrap;
}
.alice-rec__tag-key {
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-rec__tag-edit {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  background: transparent;
  border: 1px dashed var(--vp-grijs-60);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--fg-3);
  transition: border-color 0.12s ease, color 0.12s ease;
}
.alice-rec__tag-edit:hover {
  border-color: var(--vp-black);
  border-style: solid;
  color: var(--vp-black);
}

.alice-rec__cell-audio {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.alice-rec__audio-meta {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  white-space: nowrap;
}
.alice-rec__audio-sep {
  color: var(--vp-grijs-40);
}
.alice-rec__audio-gone {
  font-style: italic;
  color: var(--fg-3);
}

.alice-rec__cell-duration {
  font-family: var(--font-mono);
  font-size: 14px;
  color: var(--vp-black);
  letter-spacing: 0.04em;
}

.alice-rec__cell-created {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
}
.alice-rec__created-sub {
  font-size: 11px;
  color: var(--fg-3);
  margin-top: 2px;
}

.alice-rec__cell-status {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.alice-rec__status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  line-height: 1;
}
.alice-rec__status-dot {
  width: 6px;
  height: 6px;
  background: currentColor;
}
.alice-rec__status--transcribed {
  color: var(--vp-turquoise-160);
  background: var(--vp-turquoise-20);
}
.alice-rec__status--processing {
  color: var(--vp-geel-160);
  background: var(--vp-geel-20);
}
.alice-rec__status--uploading {
  color: var(--vp-grijs-160);
  background: var(--vp-grijs-20);
}
.alice-rec__status--failed {
  color: var(--vp-rood-160);
  background: var(--vp-rood-20);
  cursor: help;
}
.alice-rec__open {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--vp-black);
  border-radius: 0;
  flex-shrink: 0;
  transition: border-color 0.12s ease;
}
.alice-rec__row:hover .alice-rec__open,
.alice-rec__row:focus-visible .alice-rec__open {
  border-color: var(--vp-black);
}

.alice-rec__cell-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}
.alice-rec__action {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--vp-black);
  border-radius: 0;
}
.alice-rec__action:hover {
  border-color: var(--vp-black);
}
.alice-rec__action--danger {
  color: var(--vp-rood-100);
}
.alice-rec__action--danger:hover {
  border-color: var(--vp-rood-100);
  background: var(--vp-rood-10);
}

/* Row "⋯" actions menu */
.alice-rec__menu-wrap {
  position: relative;
  display: inline-flex;
}
.alice-rec__action.is-active {
  border-color: var(--vp-black);
  background: var(--bg-muted);
}
.alice-rec__menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}
.alice-rec__menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  min-width: 196px;
  display: flex;
  flex-direction: column;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  box-shadow: 4px 4px 0 var(--border-hairline);
}
.alice-rec__menu-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--vp-black);
  white-space: nowrap;
}
.alice-rec__menu-item:hover {
  background: var(--bg-muted);
}
.alice-rec__menu-item:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-rec__menu-item--danger {
  color: var(--vp-rood-100);
  border-top: 1px solid var(--border-hairline);
}
.alice-rec__menu-item--danger:hover {
  background: var(--vp-rood-10);
}
.alice-rec__menu-note {
  padding: 10px 14px;
  border-top: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  white-space: nowrap;
}

.alice-rec__empty {
  padding: 32px;
  border-bottom: 1px solid var(--border-hairline);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--fg-3);
}
.alice-rec__empty-q {
  background: var(--accent-20);
  padding: 1px 6px;
  color: var(--vp-black);
}

/* ============== Drop zone ============== */
.alice-rec__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 24px 32px 32px;
  padding: 32px;
  background: var(--vp-white);
  border: 1px dashed var(--vp-grijs-60);
  cursor: pointer;
  text-align: center;
  font-family: var(--font-ui);
  color: var(--fg-3);
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.alice-rec__dropzone:hover,
.alice-rec__dropzone.is-dragover {
  border-color: var(--vp-black);
  border-style: solid;
  color: var(--vp-black);
  background: var(--bg-muted);
}
.alice-rec__dropzone-text {
  font-size: 14px;
  font-weight: 600;
}
.alice-rec__dropzone-link {
  text-decoration: underline;
  text-underline-offset: 3px;
}
.alice-rec__dropzone-sub {
  font-size: 12px;
  color: var(--fg-3);
}

/* ============== Moments side drawer ============== */
.alice-rec__drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  background: var(--vp-white);
  border-left: 1px solid var(--border-hairline);
  display: flex;
  flex-direction: column;
  min-height: 0;
  z-index: 5;
}
.alice-rec__drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-rec__drawer-eyebrow {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 6px;
}
.alice-rec__drawer-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: var(--vp-black);
}
.alice-rec__drawer-meta {
  margin-top: 6px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-rec__drawer-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.alice-rec__drawer-export {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-rec__drawer-export:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-rec__drawer-export:disabled {
  opacity: 0.4;
  cursor: default;
}
.alice-rec__drawer-close {
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-display);
  font-size: 24px;
  line-height: 1;
  color: var(--vp-black);
  flex-shrink: 0;
}
.alice-rec__drawer-close:hover {
  border-color: var(--vp-black);
}

.alice-rec__drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.alice-rec__drawer-status {
  padding: 16px 0;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.alice-rec__drawer-status-dot {
  width: 8px;
  height: 8px;
  background: var(--accent-100);
  animation: alice-pulse 1.2s ease-in-out infinite;
}
@keyframes alice-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.alice-rec__moment {
  border: 1px solid var(--border-hairline);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.alice-rec__moment-time {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--fg-3);
  letter-spacing: 0.04em;
}
.alice-rec__moment-lead {
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 700;
  color: var(--vp-black);
  line-height: 1.35;
}
.alice-rec__moment-fields {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.alice-rec__moment-fields dt {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-rec__moment-fields dd {
  margin: 0 0 4px;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--fg-1);
  line-height: 1.5;
}
.alice-rec__moment-quotes {
  font-family: var(--font-body);
  font-size: 14px;
  font-style: italic;
  color: var(--fg-2);
  background: var(--accent-20);
  border-left: 3px solid var(--accent-100);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.alice-rec__moment-quotes p {
  margin: 0;
}
.alice-rec__moment-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.alice-rec__moment-tag {
  display: inline-flex;
  padding: 3px 8px;
  background: var(--vp-grijs-20);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-2);
}

.alice-drawer-enter-active {
  transition: transform var(--dur-base) var(--ease-drawer),
    opacity var(--dur-base) var(--ease-drawer);
}
.alice-drawer-leave-active {
  transition: transform var(--dur-exit) var(--ease-drawer),
    opacity var(--dur-exit) var(--ease-drawer);
}
.alice-drawer-enter-from,
.alice-drawer-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

/* Staggered list entrance (decorative, plays once on mount) */
@keyframes alice-rec-fade-in-up {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.alice-rec__row.is-stagger-in {
  animation: alice-rec-fade-in-up var(--dur-base) var(--ease-out) both;
}

@media (max-width: 1279px) {
  .alice-rec__head {
    padding: 24px 20px 20px;
  }
  .alice-rec__stats {
    grid-template-columns: 1fr;
  }
  .alice-rec__stat-tile + .alice-rec__stat-tile {
    border-left: none;
    border-top: 1px solid var(--border-hairline);
  }
  .alice-rec__table-head,
  .alice-rec__row {
    grid-template-columns: 40px minmax(0, 2fr) 100px 160px 104px;
    padding: 0 20px;
  }
  .alice-rec__table-head > div:nth-child(3),
  .alice-rec__table-head > div:nth-child(4),
  .alice-rec__row .alice-rec__cell-audio,
  .alice-rec__row .alice-rec__cell-duration {
    display: none;
  }
  .alice-rec__drawer {
    width: 100%;
  }
}
</style>
